// src/components/Navbar.js
import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { FiMenu, FiX, FiGlobe } from "react-icons/fi";
import { FaWhatsapp } from "react-icons/fa";

const languages = [
  { code: "tr" },
  { code: "en" },
  { code: "de" },
  { code: "fr" },
  { code: "ar" },
  { code: "pt" },
  { code: "es" },
  { code: "ru" },
  { code: "it" },
  { code: "he" },
];

const whatsappLink = "https://wa.me/905452523725";

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false); // drawer
  const [langOpen, setLangOpen] = useState(false); // language dropdown
  const [active, setActive] = useState("#neden");

  const currentLng = (i18n.resolvedLanguage || i18n.language || "tr").split(
    "-"
  )[0];

  // refs — diller: desktop & mobile
  const langWrapDesktopRef = useRef(null);
  const langWrapMobileRef = useRef(null);

  // menü başlıkları (i18n)
  const menu = [
    { title: t("nav_why"), href: "#neden" },
    { title: t("nav_before_after"), href: "#oncesi" },
    { title: t("nav_studio"), href: "#doku" },
    { title: t("nav_stories"), href: "#deneyim" },
    { title: t("nav_details"), href: "#fark" },
    { title: t("nav_contact"), href: "#iletisim" },
  ];

  // dışarı tıklayınca dil menüsünü kapat + ESC
  useEffect(() => {
    if (!langOpen) return;
    const onDocClick = (e) => {
      const d = langWrapDesktopRef.current;
      const m = langWrapMobileRef.current;
      const insideD = d && d.contains(e.target);
      const insideM = m && m.contains(e.target);
      if (!insideD && !insideM) setLangOpen(false);
    };
    const onEsc = (e) => {
      if (e.key === "Escape") setLangOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [langOpen]);

  // scroll-spy
  useEffect(() => {
    const headerOffset = 80;
    const ids = menu.map((m) => m.href.slice(1));
    const sections = ids
      .map((id) => document.getElementById(id))
      .filter(Boolean);
    if (!sections.length) return;

    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        const scrollPos = window.scrollY + headerOffset + 1;
        let current = active;
        for (const sec of sections) {
          if (
            sec.offsetTop <= scrollPos &&
            sec.offsetTop + sec.offsetHeight > scrollPos
          ) {
            current = `#${sec.id}`;
            break;
          }
        }
        if (current !== active) setActive(current);
        ticking = false;
      });
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
    // dil değişince tetiklesin
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  // smooth scroll (+ özel: #oncesi için +10px ekstra)
  const handleNavClick = (e, href) => {
    e.preventDefault();
    setOpen(false);
    setLangOpen(false);

    const target = document.querySelector(href);
    if (!target) {
      window.location.hash = href;
      return;
    }
    const headerOffset = 80;
    const extra = href === "#oncesi" ? 10 : 0; // sadece #oncesi'ye +10px
    const elementPos = target.getBoundingClientRect().top + window.pageYOffset;
    const offsetPos = elementPos - (headerOffset + extra);

    window.scrollTo({ top: offsetPos, behavior: "smooth" });
    setActive(href);
  };

  const linkCls = (href) =>
    `relative transition-colors ${
      active === href ? "text-[#c2a484]" : "text-white"
    } hover:text-[#c2a484]`;

  return (
    <header className="w-full z-[100] fixed top-0 left-0">
      <style>{`
        @keyframes shine { 0% {background-position:0% 50%} 100% {background-position:200% 50%} }
      `}</style>

      <div className="backdrop-blur-md bg-[#0b1224]/80 border-b border-white/10">
        <nav className="mx-auto max-w-7xl flex items-center justify-between px-3 md:px-4 py-2 gap-4">
          {/* Logo */}
          <a
            href="/"
            className="flex flex-col select-none"
            onClick={() => {
              setOpen(false);
              setLangOpen(false);
            }}
          >
            <span className="text-base md:text-lg font-extrabold tracking-wide text-[#e8d1b9]">
              {t("brand")}
            </span>
            <span className="text-[9px] md:text-[10px] text-[#c2a484] tracking-[0.25em] font-semibold">
              {t("clinic")}
            </span>
          </a>

          {/* Menü linkleri — SADECE >1230px */}
          <div className="hidden min-[1231px]:flex flex-1 justify-center items-center gap-4 xl:gap-5">
            {menu.map((item) => (
              <a
                key={item.href}
                href={item.href}
                onClick={(e) => handleNavClick(e, item.href)}
                className={`${linkCls(
                  item.href
                )} text-xs md:text-sm font-medium whitespace-nowrap`}
              >
                {item.title}
                <span
                  className={`absolute -bottom-1 left-0 h-[2px] w-full transition-opacity ${
                    active === item.href
                      ? "opacity-100 bg-[#c2a484]"
                      : "opacity-0"
                  }`}
                />
              </a>
            ))}
          </div>

          {/* Sağ aksiyonlar (md ve üstü) */}
          <div className="hidden md:flex items-center gap-2">
            {/* CTA — md ve üstünde görünür (telefonda yok) */}
            <a
              href={whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#1a2235] font-medium rounded-full shadow transition
                         flex items-center gap-1.5 text-xs px-3.5 py-1.5
                         [background-image:linear-gradient(90deg,#b48a5a,#e8d1b9,#b48a5a)]
                         bg-[length:200%_200%] hover:animate-[shine_1.2s_linear_infinite]
                         hover:shadow-lg hover:scale-[1.04] border border-white/10"
            >
              {t("cta_free_analysis")} <span aria-hidden>🖱️</span>
            </a>

            {/* Dil butonu — md ve üstü */}
            <div className="relative" ref={langWrapDesktopRef}>
              <button
                type="button"
                className="p-1.5 rounded-full bg-white/5 hover:bg-white/10 outline-none border border-white/10"
                onClick={() => setLangOpen((v) => !v)}
                aria-label={t("nav_languages") || "Languages"}
              >
                <FiGlobe className="text-[#e8d1b9]" size={18} />
              </button>

              {langOpen && (
                <div className="absolute right-0 mt-2 bg-[#0f1529] border border-white/10 rounded-xl shadow-lg w-28 py-1 z-[200]">
                  {languages.map((lang) => {
                    const isActive = currentLng === lang.code;
                    return (
                      <button
                        type="button"
                        key={lang.code}
                        onClick={() => {
                          i18n.changeLanguage(lang.code);
                          setLangOpen(false);
                        }}
                        className={`block w-full text-center px-3 py-1.5 text-xs transition ${
                          isActive
                            ? "text-[#c2a484] bg-white/10"
                            : "text-white/90 hover:bg-white/10 hover:text-[#c2a484]"
                        }`}
                      >
                        {lang.code.toUpperCase()}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Hamburger — SADECE md–1230px ARALIĞINDA */}
            <button
              className="hidden md:inline-flex min-[1231px]:hidden text-[#c2a484] p-2 rounded-lg hover:bg-white/10"
              onClick={() => {
                setOpen(true);
                setLangOpen(false);
              }}
              aria-label={t("nav_open_menu") || "Open menu"}
            >
              <FiMenu size={26} />
            </button>
          </div>

          {/* Mobil üst bar — sadece Dil + Hamburger (CTA yok) */}
          <div className="flex items-center gap-2 md:hidden">
            <div className="relative" ref={langWrapMobileRef}>
              <button
                type="button"
                className="p-2 rounded-full bg-white/5 hover:bg-white/10"
                onClick={() => setLangOpen((v) => !v)}
                aria-label={t("nav_languages") || "Languages"}
              >
                <FiGlobe className="text-[#e8d1b9]" size={22} />
              </button>
              {langOpen && (
                <div className="absolute right-0 mt-2 bg-[#0f1529] border border-white/10 rounded-xl shadow-lg w-32 py-2 z-[200]">
                  {languages.map((lang) => {
                    const isActive = currentLng === lang.code;
                    return (
                      <button
                        type="button"
                        key={lang.code}
                        onClick={() => {
                          i18n.changeLanguage(lang.code);
                          setLangOpen(false);
                        }}
                        className={`block w-full text-center px-4 py-2 text-sm transition ${
                          isActive
                            ? "text-[#c2a484] bg-white/10"
                            : "text-white/90 hover:bg-white/10 hover:text-[#c2a484]"
                        }`}
                      >
                        {lang.code.toUpperCase()}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <button
              className="text-[#c2a484] p-2 rounded-lg hover:bg-white/10"
              onClick={() => {
                setOpen(true);
                setLangOpen(false);
              }}
              aria-label={t("nav_open_menu") || "Open menu"}
            >
              <FiMenu size={26} />
            </button>
          </div>
        </nav>
      </div>

      {/* Mobil Drawer */}
      <div
        className={`fixed top-0 left-0 h-full z-[120] bg-[#0b1224] w-[82%] max-w-xs shadow-2xl transition-transform duration-300 ${
          open ? "translate-x-0" : "-translate-x-full"
        } flex flex-col`}
        role="dialog"
        aria-label="Mobil menü"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <div className="flex flex-col">
            <span className="text-base font-extrabold tracking-wide text-[#e8d1b9]">
              {t("brand")}
            </span>
            <span className="text-[10px] text-[#c2a484] tracking-[0.2em] font-semibold">
              {t("clinic")}
            </span>
          </div>
          <button
            onClick={() => setOpen(false)}
            className="text-[#e8d1b9] p-2 rounded-lg hover:bg-white/10"
            aria-label={t("nav_close_menu") || "Close menu"}
          >
            <FiX size={26} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {menu.map((item) => (
            <a
              key={item.href}
              href={item.href}
              onClick={(e) => handleNavClick(e, item.href)}
              className={`block text-lg font-semibold ${
                active === item.href ? "text-[#c2a484]" : "text-white"
              } hover:text-[#c2a484] transition`}
            >
              {item.title}
            </a>
          ))}

          {/* CTA — telefonda drawer içinde */}
          <a
            href={whatsappLink}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-3 block text-center rounded-full font-semibold text-[#1a2235]
                       shadow hover:scale-[1.06] transition flex items-center justify-center gap-2
                       text-sm px-3 py-2
                       [background-image:linear-gradient(90deg,#b48a5a,#e8d1b9,#b48a5a)]
                       bg-[length:220%_220%] hover:animate-[shine_1.2s_linear_infinite]
                       border border-white/10"
            onClick={() => setOpen(false)}
          >
            {t("cta_free_analysis")} <span aria-hidden>🖱️</span>
          </a>
        </div>
      </div>

      {/* Overlay — sadece drawer için */}
      {open && (
        <div
          className="fixed inset-0 z-[110] bg-black/10"
          onClick={() => setOpen(false)}
        />
      )}

      {/* WhatsApp FAB */}
      <a
        href={whatsappLink}
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-5 right-5 bg-[#25D366] rounded-full p-4 shadow-xl z-[100] hover:scale-105 transition flex items-center justify-center"
        aria-label="WhatsApp"
      >
        <FaWhatsapp size={28} color="#fff" />
      </a>
    </header>
  );
}
